<template>
	<div>
		<!-- v-if="addProfileBindData.description" -->
		<AddProfile v-bind="addProfileBindData" ref="profileManager" />
		<div>
			<v-autocomplete
				outlined
				single-line
				height="280"
				item-value="id"
				ref="autocomplete"
				v-bind="fieldProps"
				:readonly="readonly"
				@blur="isFocused = false"
				@focus="isFocused = true"
				:items="profilesToShow"
				class="profile-dropdown-ac"
				v-model="selectedProfileId"
				item-text="connection_name"
				:loading="isTestingProfile"
				:disabled="isTestingProfile"
				append-icon="mdi-chevron-down"
				label="Choose account connection"
				:menu-props="autocompleteMenuProps"
				:search-input.sync="searchProfileString"
				:class="{ 'focused-background': isFocused }"
			>
				<template v-slot:prepend-item>
					<div class="width-100 flex-align-center flex-row display-flex pl-2 connections-header">Connections</div>
				</template>
				<template v-slot:no-data>
					<div class="width-100 display-flex flex-column flex-centerize no-data-container">
						<template v-if="!isLoadingCredsForUser">
							<div>No connections here yet</div>
							<div class="no-data-description">connect your account below</div>
							<SIcon class="mt-4" name="no-connections" width="46" />
						</template>
						<AppLoader v-else />
					</div>
				</template>
				<template v-slot:append-item>
					<AnemicButton @click="addProfile" class="add-account-button" :disabled="disableRealProfiles">
						<SIcon class="mr-1 mt-1" name="add-new-accound-plus" width="13" />
						Connect a New Account
					</AnemicButton>
				</template>
				<template v-slot:item="data">
					<v-list-item :disabled="data.item.disabled" v-if="data.item.disabled || isStaled(data.item.status)">
						<template v-if="!isObject(data.item)">
							<!--eslint-disable vue/no-v-text-v-html-on-component-->
							<v-list-item-content v-text="data.item"></v-list-item-content>
						</template>
						<template v-else>
							<v-list-item-avatar v-if="isStaled(data.item.status)">
								<v-icon>mdi-alert</v-icon>
							</v-list-item-avatar>
							<v-list-item-content>
								<v-list-item-title>
									<AppTooltip :zIndex="999999999" :text="getTooltipText(data.item.different_version)">
										<Row class="flex flex-justify-space-between flex-align-center">
											<template v-if="data.item.different_version">
												<Col>
													<span class="mb-2">{{ data.item.connection_name }}</span>
													<span class="discontinued-text">{{ `Discontinued ${capitalizedServiceName} connection` }}</span>
												</Col>
											</template>
											<template v-else>{{ data.item.connection_name }} </template>
											<AppDropdownMenu left bottom absolute min-width="265" :nudge-bottom="10" :items="optionsMenuItems">
												<template #activator="{}">
													<v-btn icon x-small rounded width="20" height="20" class="field-action-button">
														<SIcon class="menu-button" name="menu-button" width="20" />
													</v-btn>
												</template>
											</AppDropdownMenu>
										</Row>
									</AppTooltip>
								</v-list-item-title>
							</v-list-item-content>
						</template>
					</v-list-item>
					<EmbeddedProfileSelectionItem v-else class="width-100" :item="data.item" />
				</template>
			</v-autocomplete>
			<div v-if="showLoadingStatus" class="connection-status flex-centerize">
				<ClipLoader color="#5F8FF1" size="18px" v-if="isTestingProfile" />
				<SIcon name="successfull-connection" width="18" v-else-if="isConnectionStatusSuccessful(selectedProfile)" />
			</div>
		</div>
		<ProfilesErrorDetailsDialog
			:errorMsg="profileErr"
			reconnectButton
			@reconnectProfile="reconnectProfile"
			:showDialog.sync="showDetailsDialog"
		/>
	</div>
</template>
<script>
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import semver from 'semver';
import ClipLoader from 'vue-spinner/src/ClipLoader.vue';
import { mapActions, mapGetters } from 'vuex';

import { ProjectPermissions, UserCredentialsStatus, CredentialsTypes } from '@ply-io/ply-front-shared-utilities';

import api from '@/api';
import EmbeddedProfileSelectionItem from '@/components/common/ProfileSelector/EmbeddedProfileSelectionItem';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { getAccountMembers } from '@/store/modules/account/actions';
import { teamMembers } from '@/store/modules/account/getters';
import { getCredentialsForService, getService } from '@/store/modules/apps/getters';
import { getUserCredentials, getCredentialData, getWorkflowLockedCredentials } from '@/store/modules/credentials/actions';
import { isLoadingCredsForUser } from '@/store/modules/credentials/getters';
import { appURL, appApiURL } from '@/store/modules/main/getters';
import { projectHasPermission, getProject } from '@/store/modules/projects/getters';
import { selectedProjectId as storedProjectId } from '@/store/modules/selectedProject/getters';
import { selectedWorkflowId } from '@/store/modules/selectedWorkflow/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import CredentialsService from '@/utils/credentials-service';
import { VIRTUAL_PROFILES_IDS, PER_USER_CONNECTION_ID, PER_ACCOUNT_CONNECTION_ID } from '@/utils/credentials-service.const';
import { buildProfileObj } from '@/utils/helpers';
import { capitalizeWord } from '@/utils/language-service';
import { ALL_PROJECTS_VIRTUAL_ID } from '@/utils/projects.const';

export default {
	name: 'ProfileSelectionDropdown',
	props: {
		profileId: { type: Number },
		serviceId: { type: Number },
		serviceVersionId: { type: String },
		customFieldProps: { type: Object },
		readonly: { type: Boolean, default: false },
		showLoadingStatus: { type: Boolean, default: false },
		shouldOpenProjectOptions: { Boolean, default: true },
		disableRealProfiles: { type: Boolean, default: false },
		includeVirtualProfiles: { type: Boolean, default: false },
		nativeIntegrationAccount: { type: Boolean, default: false },
		includeProjectRelatedConnections: { type: Boolean, default: true },
		credentialsType: { type: String, default: CredentialsTypes.Embedded },
		selectedProjectId: { type: Number },
		readOnlyCred: { type: Object }, // provided when the current selected credential does not belong to the current user
	},
	components: { ClipLoader, EmbeddedProfileSelectionItem },
	async mounted() {
		if (this.readOnlyCred || (this.isLocked && this[selectedWorkflowId.fullPath])) {
			await this[getAccountMembers.fullPath]();
			await this.setLockedCredentialData(this.selectedProfileId);
		}
	},
	computed: {
		...mapGetters([
			appURL.fullPath,
			appApiURL.fullPath,
			getService.fullPath,
			teamMembers.fullPath,
			projectHasPermission.fullPath,
			storedProjectId.fullPath,
			getProject.fullPath,
			selectedWorkflowId.fullPath, // do not use as if we always have this. we can be outside of selected workflow context
		]),
		...mapGettersShorthand({ isLoadingCredsForUser, getCredentialsForService }),
		optionsMenuItems() {
			return [{ text: `Manage`, appendIcon: 'open-in-a-new-tab', appendIconWidth: 9, onClick: this.manageConnection }];
		},
		capitalizedServiceName() {
			return capitalizeWord(this.selectedApp?.service_name);
		},
		serviceSlug() {
			return this.selectedApp?.service_slug;
		},
		autocompleteMenuProps() {
			return { contentClass: 'profile-dropdown', rounded: 'lg', closeOnContentClick: this.profileItemsList?.length > 0 };
		},
		selectedApp() {
			return this[getService.fullPath](this.serviceId);
		},
		projectId() {
			return this.selectedProjectId || this[storedProjectId.fullPath];
		},
		profileItemsList() {
			if (!this.serviceId || !this.serviceVersionId || !this.selectedApp?.version) {
				return [];
			}
			let credentialsForService = this.getCredentialsForService(this.serviceId) || [];
			if (this.disableRealProfiles) {
				credentialsForService = credentialsForService.map((creds) => ({ ...creds, disabled: true }));
			}
			const serviceVersion = this.selectedApp?.version;
			const majorVersion = semver.major(serviceVersion);
			// check if major version is the same
			const realProfiles = [...credentialsForService].map((p) =>
				semver.satisfies(p.service_version, `${majorVersion}.x`) ? p : { ...p, disabled: true, different_version: true }
			);

			let realProfilesInProject = realProfiles;
			if (this.projectId && !isNaN(this.projectId)) {
				realProfilesInProject = realProfiles.filter(
					(x) => !x.is_related || CredentialsService.isConnectionRelatedToProject(x, this.projectId)
				);
			}
			return realProfilesInProject;
		},
		profilesToShow() {
			let profiles = this.profileItemsList;
			if (!isEmpty(this.lockedCredentialData)) {
				// add the selected locked profile data
				profiles = [{ isLocked: true, ...this.lockedCredentialData }, ...this.profileItemsList];
			}
			return profiles
				.filter(
					(x) =>
						(this.includeProjectRelatedConnections && CredentialsService.isConnectionRelatedToProject(x, this.projectId)) ||
						CredentialsService.isConnectionPersonal(x)
				)
				.map((x) => ({ ...x, isProject: CredentialsService.isConnectionRelatedToProject(x, this.projectId) }));
		},
		selectedProfileIsSensitive() {
			return CredentialsService.isConnectionRelatedToProjectAndSensitive(this.selectedProfile, this.projectId);
		},
		hasSensitivePermissions() {
			if (!this.selectedProfile?.access?.related_entity_id) {
				return false;
			}
			return this[projectHasPermission.fullPath](
				this.selectedProfile.access.related_entity_id,
				ProjectPermissions.ViewSensitiveCredentials
			);
		},
		isLocked() {
			return (
				(this.selectedProfileId && isEmpty(this.selectedProfile)) || (this.selectedProfileIsSensitive && !this.hasSensitivePermissions)
			);
		},
		selectedProfile() {
			let lookedUpId = this.selectedProfileId;
			if (CredentialsService.isConnectionFromConfiguration(this.credentialsType)) {
				if (this.credentialsType === CredentialsTypes.ProfileBased) {
					lookedUpId = PER_USER_CONNECTION_ID;
				} else if (this.credentialsType === CredentialsTypes.AccountBased) {
					lookedUpId = PER_ACCOUNT_CONNECTION_ID;
				}
			}
			return this.profileItemsList.find((profile) => profile.id === lookedUpId) || {};
		},
		selectedProfileId: {
			get() {
				if (this.showDetailsDialog) {
					return this.reconnectingCredsId;
				} else if (this.isTestingProfile) {
					return this.checkingProfileId;
				} else if (this.credentialsType === CredentialsTypes.Embedded) {
					return this.profileId;
				} else if (this.credentialsType === CredentialsTypes.ProfileBased) {
					return PER_USER_CONNECTION_ID;
				} else if (this.credentialsType === CredentialsTypes.AccountBased) {
					return PER_ACCOUNT_CONNECTION_ID;
				}
				// we shouldn't be here, which credentialsType is defined?
				console.warn('credentials type of node is unknown');
				return null;
			},
			async set(value) {
				await this.connectProfile(value);
			},
		},
		addProfileBindData() {
			return buildProfileObj({ ...this.selectedApp, version: this.serviceVersionId, id: this.serviceId });
		},
		fieldProps() {
			const DEFAULT_FIELD_PROPS = { hideDetails: true };
			return { ...DEFAULT_FIELD_PROPS, ...this.customFieldProps };
		},
		lockedConnectionOwner() {
			if (!this.lockedCredentialData?.user) {
				return '';
			}
			return this[teamMembers.fullPath].find((x) => x.user_id === this.lockedCredentialData?.user)?.name || '';
		},
		lockedConnectionIsSensitive() {
			return CredentialsService.isConnectionRelatedToProjectAndSensitive(this.lockedCredentialData, this.projectId);
		},
		canChangeProjectCredentials() {
			if (this.projectId === ALL_PROJECTS_VIRTUAL_ID) {
				return true;
			}
			return this[projectHasPermission.fullPath](this.projectId, ProjectPermissions.ChangeCredentials);
		},
	},
	methods: {
		...mapActions([
			getUserCredentials.fullPath,
			getCredentialData.fullPath,
			getWorkflowLockedCredentials.fullPath,
			getAccountMembers.fullPath,
		]),
		manageConnection() {
			window.open(`/space/${this.projectId}/connections`, '_blank');
		},
		getTooltipText(differentVersion) {
			return differentVersion
				? 'This connection still works in existing steps but not new ones'
				: "Embedded credentials can't be used when module is an installable";
		},
		getAppProfiles() {
			this[getUserCredentials.fullPath]({ forceGet: true });
		},
		isConnectionStatusSuccessful(credentials) {
			return CredentialsService.isConnectionStatusSuccessful(credentials);
		},
		async checkProfile(profileId) {
			if (this.isStaled(this.selectedProfile?.status)) {
				return {
					success: false,
					errorJSON: this.selectedProfile.error_message,
				};
			} else {
				this.checkingProfileId = profileId;
				this.isTestingProfile = true;
				let ret;
				try {
					ret = await api.testConnection(profileId);
					return ret;
				} catch (error) {
					ret = {
						success: false,
						errorJSON: 'Error occurred while testing credentials',
					};
				} finally {
					this.isTestingProfile = false;
					this.checkingProfileId = null;
				}
				return ret;
			}
		},
		addProfile(params) {
			this.$refs.profileManager.startAddProfile({
				...params,
				app: this.selectedApp,
				callback: this.addProfileCallback,
				apiURL: this[appApiURL.fullPath],
				appURL: this[appURL.fullPath],
				getAppProfiles: this.getAppProfiles,
				version: this.serviceVersionId,
			});
			this.$refs['autocomplete'].isMenuActive = false;
		},
		async connectProfile(value, forceSet) {
			if ((value && value !== this.profileId) || forceSet) {
				let profileToSet;
				let validToSet = VIRTUAL_PROFILES_IDS.includes(value);
				let r = {};
				if (!validToSet) {
					// checking profile
					r = await this.checkProfile(value);
					validToSet = r.success;
				}
				// profile is valid
				if (validToSet) {
					this.resetErrorData();
					profileToSet = value;
					this.$emit('profile-set-successful');
				} else {
					this.$emit('profile-set-failed');
					this.profileErr = r.errorJSON;
					this.showDetailsDialog = true;
					this.reconnectingCredsId = value;
					profileToSet = null; // Reset.
				}
				this.$emit('select-profile', profileToSet);
			}
		},
		async forceReconnectProfile() {
			// called from parent
			await this.connectProfile(this.selectedProfileId, true);
		},
		reconnectProfile() {
			this.addProfile({ credsID: this.reconnectingCredsId, nativeIntegrationConnection: this.nativeIntegrationAccount });
		},
		async addProfileCallback(profileId) {
			if (profileId) {
				if (this.shouldOpenProjectOptions && this.canChangeProjectCredentials) {
					const newCreds = await this[getCredentialData.fullPath]({ id: profileId });
					if (this.isConnectionStatusSuccessful(newCreds)) {
						AppDialogManagerService.openUpdateProjectConnectionDialog({
							projectId: !this.projectId || isNaN(this.projectId) ? null : Number(this.projectId),
							credentialsId: profileId,
						});
					}
				}

				this.$nextTick(() => {
					this.showDetailsDialog = false;
					this.reconnectingCredsId = null;
					// Close the autocomplete todo
					this.$refs['autocomplete'].isMenuActive = false;
					this.selectedProfileId = profileId;
				});
			}
		},
		isObject(item) {
			return isObject(item);
		},
		resetErrorData() {
			this.reconnectingCredsId = null;
			this.profileErr = null;
		},
		async setLockedCredentialData(selectedProfileId) {
			if (selectedProfileId && ![PER_USER_CONNECTION_ID, PER_ACCOUNT_CONNECTION_ID].includes(selectedProfileId)) {
				if (this.readOnlyCred) {
					this.lockedCredentialData = this.readOnlyCred;
				} else {
					this.lockedCredentialData = await this[getWorkflowLockedCredentials.fullPath]({
						workflowId: this[selectedWorkflowId.fullPath],
						credentialsId: selectedProfileId,
					});
				}
			}
		},
		isStaled(status) {
			return status === UserCredentialsStatus.Staled;
		},
	},
	data: () => ({
		reconnectingCredsId: null,
		isFocused: false,
		profileErr: null,
		searchProfileString: null,
		isTestingProfile: false,
		checkingProfileId: null,
		showDetailsDialog: false,
		lockedCredentialData: {},
	}),
	watch: {
		showDetailsDialog: {
			handler(newV) {
				if (!newV) {
					// when closing details dialog, reset the related error data
					this.resetErrorData();
				}
			},
		},
		isTestingProfile(newV) {
			this.$emit('set-testing-profile', newV);
		},
		async selectedProfileId(newV, oldV) {
			if (oldV !== newV && !this.profileItemsList.find((x) => x.id === newV)) {
				await this.setLockedCredentialData(newV);
			}
		},
	},
};
</script>

<style scoped lang="scss">
.discontinued-text {
	font-size: 87%;
}

.profile-dropdown-ac {
	::v-deep {
		.v-label {
			font-size: 12px;
			margin-top: -10px;
		}
		.v-input__icon {
			margin-top: -10px;
		}
	}
}
.profile-dropdown {
	.v-list-item {
		&.v-list-item--disabled {
			&:hover {
				cursor: not-allowed;
			}
			pointer-events: all;
		}
		.v-list-item__title {
			font-size: 14px;
			font-weight: normal;
		}
	}
}

.add-account-button {
	width: 100% !important;
	height: 44px !important;
	padding: 0px !important;
	border-radius: 0px 0px 8px 8px !important;
	background: var(--ask-for-ingreation-background) !important;

	font-size: 14px !important;
	text-transform: none !important;
	color: var(--publish-button-background) !important;

	::v-deep {
		.v-btn__content {
			border-radius: 0px 0px 8px 8px !important;
		}
	}
}

.connections-header {
	height: 31px;
	font-size: 12px;
	font-weight: 600;
	background: transparent;
	border-radius: 8px 8px 0px 0px;
	color: var(--primary-font-color);
	box-shadow: 0px 1px 2px #747f8d55;
}
.no-data-container {
	height: 205px;
	font-size: 12px;
	font-weight: 600;
	color: var(--profile-selection-grey);

	.no-data-description {
		opacity: 0.5;
		font-weight: normal;
	}
}

.connection-status {
	top: -24px;
	width: 20px;
	left: 385px;
	height: 20px;
	position: relative;
}
.locked-explanation {
	background: #e5e5e5;
	font-size: 14px;
	font-weight: bold;
	border-radius: 8px;
	.locked-body {
		font-weight: 300;
		font-size: 12px;
	}
}
</style>
